import { useConfirmModal } from '@hive/component';
import { WorkspaceDialogService } from '@hive/core/modules/dialogs';
import { type I18nString, useI18n } from '@hive/i18n';
import { InformationFillDuotoneIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect } from 'react';

import {
  SubscriptionService,
  WorkspaceSubscriptionService,
} from '../../modules/cloud';
import * as styles from '../../modules/quota/views/styles.css';

interface SubscriptionRequiredMessage {
  title: I18nString;
  description: I18nString;
  confirmText: I18nString;
  tips: I18nString[];
  cancelText: I18nString;
}

const message: SubscriptionRequiredMessage = {
  title: 'com.hive.payment.subscription-required.title',
  description: 'com.hive.payment.subscription-required.description',
  confirmText: 'com.hive.payment.subscription-required.confirm',
  tips: [
    'com.hive.payment.subscription-required.tips-1',
    'com.hive.payment.subscription-required.tips-2',
  ],
  cancelText: 'Cancel',
};

const Tips = ({ tips }: { tips: I18nString[] }) => {
  const t = useI18n();
  if (!tips || tips.length < 1) {
    return null;
  }
  return (
    <div className={styles.tipsStyle}>
      {tips.map(tip => (
        <div key={tip.toString()} className={styles.tipStyle}>
          <div className={styles.bullet} />
          {t.t(tip)}
        </div>
      ))}
    </div>
  );
};

const Title = ({ title }: { title: string }) => {
  return (
    <div className={styles.modalTitle}>
      <InformationFillDuotoneIcon className={styles.errorIcon} />
      {title}
    </div>
  );
};

/**
 * Hook to check subscription status and show upgrade modal for free users.
 * Use this to block actions like creating docs, journals, collections, and folders
 * when the user doesn't have an active subscription.
 *
 * Checks both user-level subscriptions and workspace-level subscriptions (team trials).
 */
export const useRequireSubscription = () => {
  const { openConfirmModal } = useConfirmModal();
  const subscriptionService = useService(SubscriptionService);
  const workspaceSubscriptionService = useService(WorkspaceSubscriptionService);
  const workspaceDialogService = useService(WorkspaceDialogService);
  const t = useI18n();

  // Revalidate both user and workspace subscriptions on mount
  useEffect(() => {
    subscriptionService.subscription.revalidate();
    workspaceSubscriptionService.subscription.revalidate();
  }, [subscriptionService, workspaceSubscriptionService]);

  // User-level subscription (personal plans)
  const proSubscription = useLiveData(subscriptionService.subscription.pro$);
  const isUserSubLoading = useLiveData(
    subscriptionService.subscription.isRevalidating$
  );

  // Workspace-level subscription (team plans including trials)
  const workspaceSubscription = useLiveData(
    workspaceSubscriptionService.subscription.subscription$
  );
  const isWorkspaceSubLoading = useLiveData(
    workspaceSubscriptionService.subscription.isRevalidating$
  );

  const isLoading = isUserSubLoading || isWorkspaceSubLoading;

  // User has no subscription (free plan)
  // Check both user-level and workspace-level subscriptions
  // User is "free" only when they have neither a user subscription nor a workspace subscription
  const isFreeUser = !proSubscription && !workspaceSubscription && !isLoading;

  const showUpgradeModal = useCallback(() => {
    openConfirmModal({
      title: <Title title={t.t(message.title)} />,
      description: t.t(message.description),
      confirmText: t.t(message.confirmText),
      cancelText: t.t(message.cancelText),
      children: <Tips tips={message.tips} />,
      childrenContentClassName: styles.modalChildren,
      onConfirm: () => {
        workspaceDialogService.open('setting', {
          activeTab: 'plans',
          scrollAnchor: 'cloudPricingPlan',
        });
      },
      confirmButtonOptions: {
        variant: 'primary',
      },
    });
  }, [openConfirmModal, t, workspaceDialogService]);

  /**
   * Wraps an action to check subscription status first.
   * If user is on free plan, shows upgrade modal instead of executing the action.
   * @param action - The action to execute if user has subscription
   * @returns A wrapped function that checks subscription before executing
   */
  const requireSubscription = useCallback(
    <T extends (...args: any[]) => any>(action: T): T => {
      return ((...args: Parameters<T>) => {
        if (isFreeUser) {
          showUpgradeModal();
          return;
        }
        return action(...args);
      }) as T;
    },
    [isFreeUser, showUpgradeModal]
  );

  return {
    isFreeUser,
    isLoading,
    showUpgradeModal,
    requireSubscription,
  };
};
